import { z } from 'zod';
/**
 * Session scheduling ("Calendly for one-shots"). A GM proposes one or more candidate time
 * slots for the next sitting of an instance, shares a public link, and each player opens it,
 * picks the PG they play, and marks availability per slot. The GM then confirms a slot.
 */
export const availabilitySchema = z.enum(['yes', 'no', 'maybe']);
/** A candidate date/time for the next session. */
export const sessionSlotSchema = z.object({
    id: z.string(),
    /** ISO datetime string for the proposed start. */
    start: z.string(),
    /** Optional planned duration in minutes. */
    durationMin: z.number().optional(),
    note: z.string().optional(),
});
/** One player's availability for a single slot. */
export const slotResponseSchema = z.object({
    slotId: z.string(),
    availability: availabilitySchema,
});
/** A player's full RSVP: who they are (PG) + availability across all slots. */
export const sessionResponseSchema = z.object({
    id: z.string(),
    /** The PG (character) the player selected on the public page. */
    characterName: z.string(),
    characterId: z.string().optional(),
    /** Resolved from the instance roster when the chosen PG is linked to a person. */
    personId: z.string().optional(),
    /** Free-form name, used only when the responder isn't tied to a known PG. */
    displayName: z.string().optional(),
    comment: z.string().optional(),
    slots: z.array(slotResponseSchema).default([]),
    respondedAt: z.string(),
});
export const sessionPlanStatusSchema = z.enum(['open', 'scheduled', 'closed']);
/**
 * A scheduling poll for one instance. Stored server-side; the `publicToken` powers the
 * unauthenticated booking page that players open from a shared link.
 */
export const sessionPlanSchema = z.object({
    schemaVersion: z.literal(1).default(1),
    id: z.string(),
    instanceId: z.string(),
    title: z.string().default(''),
    status: sessionPlanStatusSchema.default('open'),
    /** Opaque token used in the public booking URL. */
    publicToken: z.string(),
    location: z.string().default(''),
    notes: z.string().default(''),
    slots: z.array(sessionSlotSchema).default([]),
    /** The slot the GM finalised (null until confirmed). */
    confirmedSlotId: z.string().nullable().default(null),
    responses: z.array(sessionResponseSchema).default([]),
    createdAt: z.string(),
    updatedAt: z.string(),
});
/** Lightweight plan summary for lists / dashboard. */
export const sessionPlanSummarySchema = z.object({
    id: z.string(),
    instanceId: z.string(),
    title: z.string(),
    status: sessionPlanStatusSchema,
    slotCount: z.number(),
    responseCount: z.number(),
    confirmedSlotId: z.string().nullable(),
    confirmedStart: z.string().nullable(),
    publicToken: z.string(),
    createdAt: z.string(),
    updatedAt: z.string(),
});
// --- Inputs ---------------------------------------------------------------
const slotInputSchema = z.object({
    start: z.string(),
    durationMin: z.number().optional(),
    note: z.string().optional(),
});
export const createSessionPlanInputSchema = z.object({
    instanceId: z.string(),
    title: z.string().optional(),
    location: z.string().optional(),
    notes: z.string().optional(),
    slots: z.array(slotInputSchema).optional(),
});
export const updateSessionPlanInputSchema = z.object({
    title: z.string().optional(),
    location: z.string().optional(),
    notes: z.string().optional(),
    status: sessionPlanStatusSchema.optional(),
    confirmedSlotId: z.string().nullable().optional(),
    /** Full replacement of the slot list. New slots may omit `id` (assigned server-side). */
    slots: z.array(slotInputSchema.extend({ id: z.string().optional() })).optional(),
});
// --- Public booking view --------------------------------------------------
/** A PG offered for selection on the public booking page. */
export const bookingCharacterSchema = z.object({
    name: z.string(),
    id: z.string().optional(),
    /** Name of the linked person, when the PG is already assigned in the roster. */
    personName: z.string().optional(),
});
/** What the unauthenticated booking page receives for a given token. */
export const publicSessionPlanSchema = z.object({
    token: z.string(),
    title: z.string(),
    status: sessionPlanStatusSchema,
    instanceName: z.string(),
    adventureTitle: z.string().optional(),
    location: z.string().default(''),
    notes: z.string().default(''),
    slots: z.array(sessionSlotSchema),
    confirmedSlotId: z.string().nullable(),
    characters: z.array(bookingCharacterSchema),
    responses: z.array(sessionResponseSchema),
});
/** Payload posted by a player from the public booking page. */
export const submitResponseInputSchema = z.object({
    characterName: z.string().min(1),
    characterId: z.string().optional(),
    comment: z.string().optional(),
    slots: z.array(slotResponseSchema).default([]),
});
//# sourceMappingURL=scheduling.js.map